import { FC, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Link from 'components/commercetools-ui/atoms/link';
import Slider from 'components/commercetools-ui/atoms/slider';
import { trackSelectPromotion, trackViewPromotion } from 'helpers/analytics/ga4Events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { colors } from 'helpers/textToColor/colors';
import { desktop } from 'helpers/utils/screensizes';
import { AnnouncementBarProps, Item } from './types';

const AnnouncementBar: FC<AnnouncementBarProps> = ({
  bannerBgColor1,
  bannerBgColor2,
  bgImageSrc,
  isBgImage,
  items,
  isAutoRotation,
  autoRotationInterval,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isFirst, setIsFirst] = useState(true);
  const className = clsx(['fixed left-0 top-0 z-50 w-full px-10 shadow-md'], isDesktopSize ? 'py-8 h-40' : 'py-4 h-28');

  const slidesElements = useMemo(
    () =>
      items?.map((item: Item, index) => {
        const {
          title,
          titleColor,
          titleSizeSm,
          titleSizeLg,
          target,
          ctaTextColor,
          ctaText,
          ctaFontSizeLg,
          ctaFontSizeSm,
          ctaTarget,
        } = item;
        const className = clsx([
          'font-regular',
          'min-w-0 flex-1',
          'h-full truncate text-center',
        ]);
        const defaultTitleSizeLg = 16;
        const defaultTitleSizeSm = 14;
        const defaultCtaFontSizeLg = 16; // I don't think we'd ever want the CTA text to be a different size than the title size but whatever
        const defaultCtaFontSizeSm = 14;

        const fontSize = isDesktopSize ? (titleSizeLg ?? defaultTitleSizeLg) : (titleSizeSm ?? defaultTitleSizeSm);
        const ctaFontSize = isDesktopSize ? (ctaFontSizeLg ?? defaultCtaFontSizeLg) : (ctaFontSizeSm ?? defaultCtaFontSizeSm);

        const titleComponent = (
          <h3
            style={{
              color: titleColor ?? colors.white,
              fontSize,
            }}
            className={className}
            title={title}
            data-testid="title"
            data-ffid={`lbl-condensed-banner-title-${index}`}
          >
            {title}
          </h3>
        );

        return (
          <div className="flex w-full items-center justify-center gap-8 p-4" key={index + title}>
            <div className="flex w-fit min-w-0 items-center gap-8 px-4">
              {target ? (
                <Link
                  prefetch={true}
                  considerQueryParams={true}
                  link={target}
                  title={title}
                  key={title}
                  data-testid="banner-link"
                  data-ffid={`lnk-condensed-banner-link-${index}`}
                  className="min-w-0 flex-1 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-black"
                  onClick={() => {
                    trackSelectPromotion({
                      creativeName: title || '',
                      creativeSlot: `pos_${index + 1}`,
                      promotionId: `condensedhero_banner_${index + 1}`,
                      promotionName: title || `Condensed Hero Banner ${index + 1}`,
                    });
                  }}
                >
                  {titleComponent}
                </Link>
              ) : (
                <div className="min-w-0 flex-1">{titleComponent}</div>
              )}

              {ctaTarget && ctaText && (
                <Link
                  prefetch={true}
                  considerQueryParams={true}
                  link={ctaTarget}
                  title={ctaText}
                  key={ctaText}
                  data-testid={`cta-banner-link`}
                  data-ffid={`lnk-condensed-banner-cta-${index}`}
                  className="shrink-0 underline focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-black"
                  style={{
                    fontSize: ctaFontSize,
                    color: ctaTextColor ?? colors.white,
                    textDecorationColor: ctaTextColor ?? colors.white,
                  }}
                  onClick={() => {
                    trackSelectPromotion({
                      creativeName: ctaText || '',
                      creativeSlot: `pos_${index + 1}_cta`,
                      promotionId: `condensedhero_banner_cta_${index + 1}`,
                      promotionName: ctaText || `Condensed Hero Banner CTA ${index + 1}`,
                    });
                  }}
                >
                  {ctaText}
                </Link>
              )}
            </div>
          </div>
        );
      }),
    [items, isDesktopSize],
  );
  useEffect(() => {
    if (isFirst && items && items?.length > 0) {
      trackViewPromotion({
        creativeName: items?.[0].title || undefined,
        creativeSlot: `pos_${1}`,
        promotionId: `condensedhero_banner_${1}`,
        promotionName: items?.[0].title || `Condensed Hero Banner ${1}`,
      });
    }
  }, [items]);

  return (
    <div
      className={className}
      style={{
        backgroundImage:
          isBgImage && bgImageSrc
            ? `url(${bgImageSrc})`
            : `linear-gradient(180deg, ${bannerBgColor1} 0%, ${bannerBgColor2} 100%)`,
        ...(isBgImage &&
          bgImageSrc && {
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }),
      }}
      data-testid="announcement-bar"
    >
      <Slider 
        a11y={{
          enabled: true,
          containerRoleDescriptionMessage: '',
          itemRoleDescriptionMessage: '',
          slideRole: 'presentation',
        }}
        onInit={(swiper) => {
          swiper.slides.forEach((slide) => {
            slide.removeAttribute('tabindex');
          });
        }}
        dots={false}
        autoplay={
          isAutoRotation
            ? { delay: Math.max(0, autoRotationInterval ?? 0) * 1000 }
            : false
        }
        arrows={true}
        allowTouchMove={true}
        simulateTouch={false}
        touchRatio={0}
        loop={false}
        freeMode={false}
        slidesPerView={1}
        uniqueFFIDText="announcementBar"
        spaceBetween={0}
        containerClassName="container md:container sm:container h-full flex items-center px-18"
        className="flex"
        allowArrowsOnTouchDevice
        gridNavigation={false}
        blackBGForArrows={isBgImage}
        data-ffid="ctr-condensed-banner-slider"
        onSlideChange={(swiper) => {
          setIsFirst(false);
          trackViewPromotion({
            creativeName: items?.[swiper.activeIndex].title || undefined,
            creativeSlot: `pos_${swiper.activeIndex + 1}`,
            promotionId: `condensedhero_banner_${swiper.activeIndex + 1}`,
            promotionName: items?.[swiper.activeIndex].title || `Condensed Hero Banner ${swiper.activeIndex + 1}`,
          });
          swiper.slides.forEach((slide) => {
            slide.removeAttribute('tabindex');
          });
        }}
      >
        {slidesElements}
      </Slider>
    </div>
  );
};

export default AnnouncementBar;
